import React, { useEffect, useState, Suspense } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@platform-ui/zds-next';
import { Box } from '@mui/material';
import { darkTheme } from './theme';
import { NavigationRootInner } from './NavigationRootInner';
import { designTokens } from '@platform-ui/design-system';
import { NavigationOverrides } from './NavigationContext';
import { isLayoutServiceNavNestedDuplicate } from './utils/layoutServiceNavDuplicate';
import { useGlobalNavFlag } from './flags';
import { GlobalNavRoot } from './GlobalNav/GlobalNavRoot';
import { NavigationLoadingMask } from './NavigationLoadingMask';
import { isSalesforceHost } from './utils/isSalesforceHost';
const LazyConciergeAgent = React.lazy(() => import('./Concierge'));

/* Styles ordinarily set by MUI <CssBaseline /> component
 * but those styles target 'body', and we are trying to
 * keep component styles isolated in shadow DOM
 */
const BaseStyles: React.FC<{ aiOnly: boolean }> = ({
  aiOnly = false,
  children,
}) => {
  return (
    <Box
      sx={{
        fontFamily: designTokens.fonts.fontFamily,
        fontSize: '0.875rem',
        fontWeight: '400',
        WebkitFontSmoothing: 'antialiased',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(!aiOnly && {
          minHeight: '100vh',
        }),
      }}
    >
      {children}
    </Box>
  );
};

export interface NavigationProps {
  emotionMount?: HTMLDivElement;
  overrides?: NavigationOverrides;
  id?: string | null;
  /**
   * When true, suppresses overlapping chrome (e.g. environment bar) for this
   * instance. Set when `z-unified-nav-v2` is nested inside another copy, or
   * combined with duplicate-mount detection (`isLayoutServiceNavNestedDuplicate`).
   *
   * @deprecated Interim API for the layout-service / upstream overlap. Prefer not
   * to add new usages; it exists only to manage duplicate chrome until the
   * migration finishes. **Will be removed** once upstreams fully own nav
   * injection and nested/duplicate mounts are no longer a concern.
   *
   * Historical context: Zuora once centralized nav injection via **layout service**
   * (a shared HTML touchpoint across many servers and small SPA shells) so not
   * every upstream had to change at once. The intended end state is that each
   * upstream renders the custom element itself, then layout service drops it—
   * fully retiring layout service from the architecture. That migration is
   * incomplete: some upstreams still rely on layout service; removing it there
   * would leave those pages with no navbar. During the overlap, both layout
   * service and an upstream can mount the element; this flag keeps duplicate
   * chrome from stacking until the refactor finishes.
   */
  suppressNestedDuplicateChrome?: boolean;
  aiOnly: boolean;
}

export const Navigation = ({
  emotionMount,
  overrides,
  id,
  suppressNestedDuplicateChrome: suppressNestedDuplicateChromeProp,
  aiOnly,
}: NavigationProps) => {
  const { isGlobalNavEnabled, isGlobalNavConfirmed, isLoading } =
    useGlobalNavFlag({
      overrides,
    });
  /* Salesforce LWC embeds the agent on a salesforce.com / lightning host and
   * does not set `ai-only`. Without this, the Global Nav guess mounts the
   * loading skeleton beside the chat and it never goes away — there is no
   * Billing identity or `/v1/navigation` in that frame. */
  const isAiOnlyShell = aiOnly || isSalesforceHost();

  const [emotionCache] = useState(() =>
    createCache({
      key: 'unified-nav',
      prepend: true,
      container: emotionMount,
      /* Never enable speedy mode here. The speedy path inserts through
       * CSSStyleSheet.insertRule, and Emotion's `sheetForTag` cannot resolve the
       * sheet for a <style> that lives in a shadow root — its fallback scans
       * document.styleSheets, which by definition never contains one. The
       * production build of @emotion/sheet then swallows the resulting throw in
       * an empty catch, so rules are dropped silently and permanently. That was
       * ZUI-2706: unstyled, unclickable nav menus on Safari 15. Still unfixed as
       * of @emotion/sheet@1.4.0, and the nav now has four shadow-root caches
       * relying on this. */
      speedy: false,
    }),
  );

  useEffect(() => {
    document.dispatchEvent(new CustomEvent('navRendered', { detail: { id } }));
  }, [id]);

  useEffect(() => {
    if (!isGlobalNavEnabled) {
      return;
    }

    const styleId = 'hide-qualtrics-feedback-button';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        #QSIFeedbackButton-btn {
          display: none !important;
        }
        #QSIFeedbackButton-target-container {
          display: none;
          left: 40px !important;
          bottom: 0 !important;
          border-right: 2px solid rgb(166, 166, 166) !important;
        }
      `;
      document.head.appendChild(styleElement);
    }

    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, [isGlobalNavEnabled]);

  const suppressNestedDuplicateChrome =
    suppressNestedDuplicateChromeProp || isLayoutServiceNavNestedDuplicate(id);

  return (
    <CacheProvider value={emotionCache}>
      {isLoading && !isAiOnlyShell ? <NavigationLoadingMask /> : null}
      {/* Render the new global nav if the flag is enabled, otherwise render the other version */}
      {isGlobalNavEnabled ? (
        isAiOnlyShell ? (
          <>
            <slot name="breadcrumbContent" />
            <slot name="pageContent" />
            <Suspense fallback={<React.Fragment />}>
              <LazyConciergeAgent />
            </Suspense>
          </>
        ) : (
          <GlobalNavRoot
            overrides={overrides}
            hideEnvironmentBar={suppressNestedDuplicateChrome}
            hideAiAgent={suppressNestedDuplicateChrome}
            suppressBillingEnvironmentBanner={isGlobalNavEnabled}
            isGlobalNavConfirmed={isGlobalNavConfirmed}
          />
        )
      ) : (
        <ThemeProvider theme={darkTheme}>
          <BaseStyles aiOnly={isAiOnlyShell}>
            {isAiOnlyShell ? (
              <>
                <slot name="breadcrumbContent" />
                <slot name="pageContent" />
                <Suspense fallback={<React.Fragment />}>
                  <LazyConciergeAgent />
                </Suspense>
              </>
            ) : (
              <NavigationRootInner
                overrides={overrides}
                hideEnvironmentBar={suppressNestedDuplicateChrome}
                hideAiAgent={suppressNestedDuplicateChrome}
                suppressBillingEnvironmentBanner={isGlobalNavEnabled}
              />
            )}
          </BaseStyles>
        </ThemeProvider>
      )}
    </CacheProvider>
  );
};
